import React, { useEffect, useRef } from 'react';
import { DmsButton, intl } from 'dmsbasereact';
import { Box } from '@mui/material';
import { useCheckinCheckoutForm } from './useCheckinCheckoutForm';
import { InvoiceDetailsSection } from './sections/InvoiceDetailsSection';
import { ProductDetailsSection } from './sections/ProductDetailsSection';
import { DateSection } from './sections/DateSection';
import { NotesSection } from './sections/NotesSection';

export const CheckinCheckoutForm = ({ organizationBranches = [], batches = [], mode = 'checkin', hideButton = false, onDataChange, onCheckoutSubmitted, initialData = null, fieldHasError, helperText, submitRef }) => {
    const { state, handlers } = useCheckinCheckoutForm({ onDataChange, onCheckoutSubmitted, mode, organizationBranches, initialData });
    const {
        danfeNumber,
        ownerEntityPk,
        destinationEntityPk,
        products,
        receivedAt,
        sentAt,
        notes,
        isSubmitting,
        isCheckin,
        stockRefreshKey,
        // Check-in specific
        checkinInvoices,
        isLoadingCheckinInvoices,
        selectedCheckinInvoice,
        // Check-out specific
        checkoutDestinations,
        checkoutOrigins,
        isLoadingDestinations,
        isLoadingOrigins,
        checkoutBatchesWithStock,
        // Batches
        batchesWithPart,
    } = state;

    // Use filtered batches for checkout (only those with stock), all batches for checkin
    const availableBatches = isCheckin ? batchesWithPart : checkoutBatchesWithStock;

    const {
        setDanfeNumber,
        setOwnerEntityPk,
        setDestinationEntityPk,
        addProduct,
        removeProduct,
        updateProduct,
        setReceivedAt,
        setSentAt,
        setNotes,
        handleSubmit,
        handleCheckinInvoiceSelect,
        handleXmlImport,
    } = handlers;

    const xmlInputRef = useRef(null);

    // Expose submit and isSubmitting to parent via ref
    useEffect(() => {
        if (submitRef) {
            submitRef.current = { handleSubmit, isSubmitting };
        }
    }, [submitRef, handleSubmit, isSubmitting]);

    return (
        <Box>
            {!isCheckin && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <input
                        ref={xmlInputRef}
                        type="file"
                        accept=".xml"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleXmlImport(file);
                            e.target.value = '';
                        }}
                    />
                    <DmsButton
                        variant="outlined"
                        onClick={() => xmlInputRef.current?.click()}
                        text={intl.get('ImportXml').d('Import XML')}
                    />
                </Box>
            )}

            <InvoiceDetailsSection
                isCheckin={isCheckin}
                danfeNumber={danfeNumber}
                setDanfeNumber={setDanfeNumber}
                ownerEntityPk={ownerEntityPk}
                setOwnerEntityPk={setOwnerEntityPk}
                destinationEntityPk={destinationEntityPk}
                setDestinationEntityPk={setDestinationEntityPk}
                organizationBranches={organizationBranches}
                // Check-in specific
                checkinInvoices={checkinInvoices}
                isLoadingCheckinInvoices={isLoadingCheckinInvoices}
                selectedCheckinInvoice={selectedCheckinInvoice}
                handleCheckinInvoiceSelect={handleCheckinInvoiceSelect}
                // Check-out specific
                checkoutDestinations={checkoutDestinations}
                checkoutOrigins={checkoutOrigins}
                isLoadingDestinations={isLoadingDestinations}
                isLoadingOrigins={isLoadingOrigins}
                intl={intl}
                fieldHasError={fieldHasError}
                helperText={helperText}
            />

            <ProductDetailsSection
                products={products}
                addProduct={addProduct}
                removeProduct={removeProduct}
                updateProduct={updateProduct}
                batchesWithPart={availableBatches}
                intl={intl}
                ownerEntityPk={ownerEntityPk}
                isCheckin={isCheckin}
                stockRefreshKey={stockRefreshKey}
                fieldHasError={fieldHasError}
                helperText={helperText}
            />

            <DateSection
                isCheckin={isCheckin}
                receivedAt={receivedAt}
                sentAt={sentAt}
                setReceivedAt={setReceivedAt}
                setSentAt={setSentAt}
                intl={intl}
            />

            <NotesSection
                notes={notes}
                setNotes={setNotes}
                intl={intl}
            />

            {!hideButton &&
                <Box sx={{ mt: 3 }}>
                    <DmsButton
                        variant="primary"
                        onClick={() => handleSubmit()}
                        text={isSubmitting ? intl.get('Processing...').d('Processing...') : (isCheckin ? intl.get('SubmitCheckin').d('Submit Check-in') : intl.get('SubmitCheckOut').d('Submit Check-out'))}
                        disabled={isSubmitting}
                        fullWidth
                    />
                </Box>
            }
        </Box>
    );
};
